import React, { useEffect, useState } from 'react';
import TransportAccessForm from './TransportAccessForm';
import { useDispatch, useSelector } from 'react-redux';
import { GetTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';
import { Edit, Trash2, X, Plus, ChevronLeft, ChevronRight } from 'lucide-react';

function TransportAccess({ projectiondataID }) {
  const [locations, setLocations] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);

  const { data, isError, isSuccess } = useSelector((s) => s.GetTransportDetail); 
  const dispatch = useDispatch();          

  useEffect(() => {
    if (projectiondataID && !data) {
      dispatch(GetTransportDetail(projectiondataID));
    }
  }, [dispatch, projectiondataID]);

  useEffect(() => {
    if (isSuccess && data) {
      setLocations(data.data?.transport_detail || []);
      setCurrentPage(1);
    }
  }, [isSuccess, data]);

  // Pagination logic
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = locations.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(locations.length / itemsPerPage);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const handleAddTransport = (newLocation) => {
    const formattedDistance = `${newLocation.distance} km`;
    setLocations(prev => [...prev, {
      type: newLocation.type,
      name: newLocation.name,
      distance: formattedDistance,
    }]);
    setCurrentPage(Math.ceil((locations.length + 1) / itemsPerPage));
  };

  const handleDeleteConfirmation = (location) => {
    setLocationToDelete(location);
    setShowDeleteConfirmation(true);
  };

  const handleDelete = () => {
    const newLocations = locations.filter(location => location !== locationToDelete);
    setLocations(newLocations);
    if (currentPage > 1 && currentItems.length === 1) {
      setCurrentPage(currentPage - 1);
    }
    setShowDeleteConfirmation(false);
  };

  return (
    <div className="p-4 bg-gray-800 rounded-lg">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-semibold">Transportation Access</h3>
        <button
          onClick={() => setShowForm(true)}
          className="p-1.5 bg-blue-600 hover:bg-blue-700 rounded-full border border-blue-500"
          title="Add Transport"
        >
          <Plus className="w-4 h-4" />
        </button>
      </div>

      <TransportAccessForm
        showForm={showForm}
        setShowForm={setShowForm}
        onAddTransport={handleAddTransport}
        projectiondataID={projectiondataID}
      />

      <div className="text-sm border border-gray-700 rounded-lg overflow-hidden">
        {locations.length > 0 ? (
          <>
            <table className="w-full">
              <thead>
                <tr className="bg-gray-750 text-gray-300 border-b border-gray-700">
                  <th className="px-3 py-2 text-left border-r border-gray-700">Type</th>
                  <th className="px-3 py-2 text-left border-r border-gray-700">Name</th>
                  <th className="px-3 py-2 text-left border-r border-gray-700">Distance</th>
                  <th className="px-3 py-2 text-left">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.map((location, index) => (
                  <tr 
                    key={index} 
                    className="border-b border-gray-700 hover:bg-gray-750 last:border-b-0"
                  >
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{location.type}</td>
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{location.name}</td>
                    <td className="px-3 py-2 border-r border-gray-700 text-left">{location.distance}</td>
                    <td className="px-3 py-2">
                      <div className="flex space-x-2">
                        <button className="text-blue-400 hover:text-blue-300">
                          <Edit className="w-3.5 h-3.5" />
                        </button>
                        <button 
                          onClick={() => handleDeleteConfirmation(location)}
                          className="text-red-400 hover:text-red-300"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* Pagination controls */}
            <div className="flex items-center justify-between px-3 py-2 bg-gray-750 border-t border-gray-700">
              <div className="text-xs text-gray-400">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, locations.length)} of {locations.length}
              </div>
              <div className="flex space-x-1">
                <button
                  onClick={() => paginate(currentPage - 1)}
                  disabled={currentPage === 1}
                  className={`p-1.5 rounded border ${currentPage === 1 ? 'bg-gray-700 border-gray-600' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                </button>
                
                {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
                  <button
                    key={number}
                    onClick={() => paginate(number)}
                    className={`p-1.5 px-2.5 rounded border ${currentPage === number ? 'bg-blue-600 border-blue-500' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                  >
                    {number}
                  </button>
                ))}
                
                <button
                  onClick={() => paginate(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className={`p-1.5 rounded border ${currentPage === totalPages ? 'bg-gray-700 border-gray-600' : 'bg-gray-600 border-gray-500 hover:bg-gray-550'}`}
                >
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="text-center py-4 text-gray-400">
            <button
              onClick={() => setShowForm(true)}
              className="text-blue-400 hover:text-blue-300 text-sm"
            >
              + Add Transport Detail
            </button>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-4 w-full max-w-xs border border-gray-700">
            <div className="flex justify-between items-center mb-3 border-b border-gray-700 pb-2">
              <h3 className="font-medium">Confirm Deletion</h3>
              <button onClick={() => setShowDeleteConfirmation(false)} className="text-gray-400">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-300 mb-4">
              Delete this transport detail?
            </p>
            <div className="flex justify-end space-x-2 border-t border-gray-700 pt-3">
              <button
                onClick={() => setShowDeleteConfirmation(false)}
                className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 rounded border border-gray-500"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="px-3 py-1.5 text-sm bg-red-600 hover:bg-red-700 rounded flex items-center border border-red-500"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1" />
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TransportAccess;

// import React, { useEffect, useState } from 'react';
// import TransportAccessForm from './TransportAccessForm';
// import { useDispatch, useSelector } from 'react-redux';
// import { GetTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';
// import { Edit, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';

// function TransportAccess({ projectiondataID }) {
//   const [locations, setLocations] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
//   const [locationToDelete, setLocationToDelete] = useState(null);
  
//   // Pagination state
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(5); // You can adjust this number

//   const { data, isError, isSuccess } = useSelector((s) => s.GetTransportDetail); 
//   const dispatch = useDispatch();          

//   useEffect(() => {
//     if (projectiondataID && !data) {
//       dispatch(GetTransportDetail(projectiondataID));
//     }
//   }, [dispatch, projectiondataID]);

//   useEffect(() => {
//     if (isSuccess && data) {
//       setLocations(data.data?.transport_detail || []);
//       setCurrentPage(1); // Reset to first page when data changes
//     }
//   }, [isSuccess, data]);

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = locations.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(locations.length / itemsPerPage);

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const handleAddTransport = (newLocation) => {
//     const formattedDistance = `${newLocation.distance} km`;

//     setLocations(prev => {
//       // Ensure prev is always an array
//       const previousLocations = Array.isArray(prev) ? prev : [];
//       return [
//         ...previousLocations,
//         {
//           type: newLocation.type,
//           name: newLocation.name,
//           distance: formattedDistance,
//         }
//       ];
//     });
//     setCurrentPage(Math.ceil((locations.length + 1) / itemsPerPage)); // Go to last page
//   };

//   const handleDeleteConfirmation = (location) => {
//     setLocationToDelete(location);
//     setShowDeleteConfirmation(true);
//   };

//   const handleDelete = () => {
//     const newLocations = locations.filter((location) => location !== locationToDelete);
//     setLocations(newLocations);
    
//     // Adjust current page if needed
//     if (currentPage > 1 && currentItems.length === 1) {
//       setCurrentPage(currentPage - 1);
//     }
    
//     setShowDeleteConfirmation(false);
//   };

//   const handleCancelDelete = () => {
//     setShowDeleteConfirmation(false);
//   };

//   return (
//     <div className="p-4 text-white">
//       <div className="relative flex items-center mb-4">
//         <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//           Transportation Access
//         </h3>
//         <button
//           onClick={() => setShowForm(true)}
//           className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//           </svg>
//         </button>
//       </div>

//       <TransportAccessForm
//         showForm={showForm}
//         setShowForm={setShowForm}
//         onAddTransport={handleAddTransport}
//         projectiondataID={projectiondataID}
//       />

//       <div className="flex flex-col items-center">
//         {locations && locations.length > 0 ? (
//           <>
//             <table className="border border-gray-600 text-sm w-full mb-4">
//               <thead className="bg-gray-800 text-gray-200">
//                 <tr>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {currentItems.map((location, index) => (
//                   <tr key={index} className="hover:bg-gray-700">
//                     <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                     <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                     <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
//                     <td className="border border-gray-600 px-4 py-2">
//                       <button
//                         className="text-blue-500 hover:text-blue-700 mr-3">
//                         <Edit className="w-3 h-3" />
//                       </button>
//                       <button 
//                         onClick={() => handleDeleteConfirmation(location)}
//                         className="text-red-500 hover:text-red-700">
//                         <Trash2 className="w-3 h-3" />
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>

//             {/* Pagination controls */}
//             <div className="flex items-center justify-between w-full mt-4">
//               <div className="text-sm text-gray-400">
//                 Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, locations.length)} of {locations.length} entries
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                   className={`p-2 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                 >
//                   <ChevronLeft className="w-4 h-4" />
//                 </button>
                
//                 {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
//                   <button
//                     key={number}
//                     onClick={() => paginate(number)}
//                     className={`p-2 px-3 rounded-md ${currentPage === number ? 'bg-blue-500 text-white' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                   >
//                     {number}
//                   </button>
//                 ))}
                
//                 <button
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                   className={`p-2 rounded-md ${currentPage === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                 >
//                   <ChevronRight className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//             No transport details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>

//       {showDeleteConfirmation && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={handleCancelDelete}
//                 className="text-gray-400 hover:text-white">
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete this transport detail? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={handleCancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//               >
//                 <Trash2 className="w-4 h-4 mr-2" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default TransportAccess;
// import React, { useEffect, useState } from 'react';
// import TransportAccessForm from './TransportAccessForm';
// import { useDispatch, useSelector } from 'react-redux';
// import { GetTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';
// import { Edit, Trash2, X, ChevronLeft, ChevronRight } from 'lucide-react';

// function TransportAccess({ projectiondataID }) {
//   const [locations, setLocations] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
//   const [locationToDelete, setLocationToDelete] = useState(null);
  
//   // Pagination state
//   const [currentPage, setCurrentPage] = useState(1);
//   const [itemsPerPage] = useState(5); // You can adjust this number

//   const { data, isError, isSuccess } = useSelector((s) => s.GetTransportDetail); 
//   const dispatch = useDispatch();          

//   useEffect(() => {
//     if (projectiondataID && !data) {
//       dispatch(GetTransportDetail(projectiondataID));
//     }
//   }, [dispatch, projectiondataID]);

//   useEffect(() => {
//     if (isSuccess && data) {
//       setLocations(data.data?.transport_detail || []);
//       setCurrentPage(1); // Reset to first page when data changes
//     }
//   }, [isSuccess, data]);

//   // Pagination logic
//   const indexOfLastItem = currentPage * itemsPerPage;
//   const indexOfFirstItem = indexOfLastItem - itemsPerPage;
//   const currentItems = locations.slice(indexOfFirstItem, indexOfLastItem);
//   const totalPages = Math.ceil(locations.length / itemsPerPage);

//   const paginate = (pageNumber) => setCurrentPage(pageNumber);

//   const handleAddTransport = (newLocation) => {
//     const formattedDistance = `${newLocation.distance} km`;

//     setLocations(prev => [
//       ...(prev || []),
//       {
//         type: newLocation.type,
//         name: newLocation.name,
//         distance: formattedDistance,
//       }
//     ]);
//     setCurrentPage(Math.ceil((locations.length + 1) / itemsPerPage)); // Go to last page
//   };

//   const handleDeleteConfirmation = (location) => {
//     setLocationToDelete(location);
//     setShowDeleteConfirmation(true);
//   };

//   const handleDelete = () => {
//     const newLocations = locations.filter((location) => location !== locationToDelete);
//     setLocations(newLocations);
    
//     // Adjust current page if needed
//     if (currentPage > 1 && currentItems.length === 1) {
//       setCurrentPage(currentPage - 1);
//     }
    
//     setShowDeleteConfirmation(false);
//   };

//   const handleCancelDelete = () => {
//     setShowDeleteConfirmation(false);
//   };

//   return (
//     <div className="p-4 text-white">
//       <div className="relative flex items-center mb-4">
//         <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//           Transportation Access
//         </h3>
//         <button
//           onClick={() => setShowForm(true)}
//           className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//           </svg>
//         </button>
//       </div>

//       <TransportAccessForm
//         showForm={showForm}
//         setShowForm={setShowForm}
//         onAddTransport={handleAddTransport}
//         projectiondataID={projectiondataID}
//       />

//       <div className="flex flex-col items-center">
//         {locations && locations.length > 0 ? (
//           <>
//             <table className="border border-gray-600 text-sm w-full mb-4">
//               <thead className="bg-gray-800 text-gray-200">
//                 <tr>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//                   <th className="border border-gray-600 px-4 py-2 text-left">Action</th>
//                 </tr>
//               </thead>
//               <tbody>
//                 {currentItems.map((location, index) => (
//                   <tr key={index} className="hover:bg-gray-700">
//                     <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                     <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                     <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
//                     <td className="border border-gray-600 px-4 py-2">
//                       <button
//                         className="text-blue-500 hover:text-blue-700 mr-3">
//                         <Edit className="w-3 h-3" />
//                       </button>
//                       <button 
//                         onClick={() => handleDeleteConfirmation(location)}
//                         className="text-red-500 hover:text-red-700">
//                         <Trash2 className="w-3 h-3" />
//                       </button>
//                     </td>
//                   </tr>
//                 ))}
//               </tbody>
//             </table>

//             {/* Pagination controls */}
//             <div className="flex items-center justify-between w-full mt-4">
//               <div className="text-sm text-gray-400">
//                 Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, locations.length)} of {locations.length} entries
//               </div>
//               <div className="flex space-x-2">
//                 <button
//                   onClick={() => paginate(currentPage - 1)}
//                   disabled={currentPage === 1}
//                   className={`p-2 rounded-md ${currentPage === 1 ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                 >
//                   <ChevronLeft className="w-4 h-4" />
//                 </button>
                
//                 {Array.from({ length: totalPages }, (_, i) => i + 1).map(number => (
//                   <button
//                     key={number}
//                     onClick={() => paginate(number)}
//                     className={`p-2 px-3 rounded-md ${currentPage === number ? 'bg-blue-500 text-white' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                   >
//                     {number}
//                   </button>
//                 ))}
                
//                 <button
//                   onClick={() => paginate(currentPage + 1)}
//                   disabled={currentPage === totalPages}
//                   className={`p-2 rounded-md ${currentPage === totalPages ? 'bg-gray-700 text-gray-500 cursor-not-allowed' : 'bg-gray-600 text-white hover:bg-gray-500'}`}
//                 >
//                   <ChevronRight className="w-4 h-4" />
//                 </button>
//               </div>
//             </div>
//           </>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//             No transport details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>

//       {showDeleteConfirmation && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 onClick={handleCancelDelete}
//                 className="text-gray-400 hover:text-white">
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete this transport detail? This action cannot be undone.
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 onClick={handleCancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 onClick={handleDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//               >
//                 <Trash2 className="w-4 h-4 mr-2" />
//                 Delete
//               </button>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }

// export default TransportAccess;
// import React, { useEffect, useState } from 'react';
// import TransportAccessForm from './TransportAccessForm';
// import { useDispatch, useSelector } from 'react-redux';
// import { GetTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';
// import { Edit, Trash2, X } from 'lucide-react'; // Import Lucide React icons

// function TransportAccess({ projectiondataID }) {
//   const [locations, setLocations] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
//   const [locationToDelete, setLocationToDelete] = useState(null); // To track which item to delete
 
//   const { data, isError , isSuccess } = useSelector((s) => s.GetTransportDetail); 

//   const dispatch = useDispatch();          

//   useEffect(() => {
//     if (projectiondataID && !data) {
//       dispatch(GetTransportDetail(projectiondataID));
//     }
//   }, [dispatch, projectiondataID]);

//   useEffect(() => {
//     setLocations(data?.data || []);
//   }, [isSuccess]);

//   const handleAddTransport = (newLocation) => {
//     const formattedDistance = `${newLocation.distance} km`;

//     setLocations((prev) => [
//       ...prev,
//       {
//         type: newLocation.type,
//         name: newLocation.name,
//         distance: formattedDistance,
//       }
//     ]);
//     const transportData = {
//       transport_detail: [
//         ...locations.map((loc) => ({
//           type: loc.type,
//           name: loc.name,
//           Distance: parseFloat(loc.distance.split(' ')[0])
//         })),
//         {
//           type: newLocation.type,
//           name: newLocation.name,
//           Distance: parseFloat(newLocation.distance)
//         }
//       ]
//     };

//     console.log("Data for backend:", transportData);
//   };

//   const handleDeleteConfirmation = (location) => {
//     setLocationToDelete(location);
//     setShowDeleteConfirmation(true);
//   };

//   const handleDelete = () => {
//     setLocations(locations.filter((location) => location !== locationToDelete));
//     setShowDeleteConfirmation(false);
//   };

//   const handleCancelDelete = () => {
//     setShowDeleteConfirmation(false);
//   };

//   return (
//     <div className="p-4 text-white">
//       <div className="relative flex items-center mb-4">
//         <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//           Transportation Access
//         </h3>
//         <button
//           onClick={() => setShowForm(true)}
//           className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//           </svg>
//         </button>
//       </div>

//       <TransportAccessForm
//         showForm={showForm}
//         setShowForm={setShowForm}
//         onAddTransport={handleAddTransport}
//         projectiondataID={projectiondataID}
//       />

//       <div className="flex justify-center">
//         {locations && locations?.transport_detail ? (
//           <table className="border border-gray-600 text-sm">
//             <thead className="bg-gray-800 text-gray-200">
//               <tr>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Action</th>
//               </tr>
//             </thead>
//             <tbody>
//               {locations && locations?.transport_detail.map((location, index) => (
//                 <tr key={index} className="hover:bg-gray-700">
//                   <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
//                   <td className="border border-gray-600 px-4 py-2">
//                     <button
//                       className="text-blue-500 hover:text-blue-700 mr-3">
//                       <Edit className="w-3 h-3" />
//                     </button>
//                     <button 
//                       onClick={() => handleDeleteConfirmation(location)}
//                       className="text-red-500 hover:text-red-700">
//                       <Trash2 className="w-3 h-3" />
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//             No transport details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>

//       {/* Delete Confirmation Popup */}
//       {showDeleteConfirmation  && (
//         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//           <div 
//             // ref={modalRef}
//             className="bg-gray-800 rounded-lg p-6 w-full max-w-md transform transition-all border border-gray-700"
//           >
//             <div className="flex justify-between items-center mb-4">
//               <h3 className="text-xl font-semibold text-white">Confirm Deletion</h3>
//               <button 
//                 // onClick={cancelDelete}
//                 className="text-gray-400 hover:text-white"
//                 onClick={handleCancelDelete} >
//                 <X className="w-6 h-6" />
//               </button>
//             </div>
//             <p className="text-gray-300 mb-6">
//               Are you sure you want to delete project? This action cannot be undone.
//               {/* 😀😀 */}
//             </p>
//             <div className="flex justify-end space-x-4">
//               <button
//                 // onClick={cancelDelete}
//                 onClick={handleCancelDelete}
//                 className="px-4 py-2 rounded-md bg-gray-600 text-white hover:bg-gray-700 transition-colors border border-gray-500"
//               >
//                 Cancel
//               </button>
//               <button
//                 // onClick={confirmDelete}
//                 onClick={handleDelete}
//                 className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 transition-colors flex items-center border border-red-700"
//                 // disabled={isLoading}
//               >
//                 {null ? (
//                   <>
//                     <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
//                       <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
//                       <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
//                     </svg>
//                     Deleting...
//                   </>
//                 ) :
//                  (
//                   <>
                         
//                     <Trash2 className="w-4 h-4 mr-2" />
//                     Delete
//                   </>
//                 )}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//     </div>
//   );
// }

// export default TransportAccess;



// import React, { useEffect, useState } from 'react';
// import TransportAccessForm from './TransportAccessForm';
// import { useDispatch, useSelector } from 'react-redux';
// import { GetTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';

// function TransportAccess({ projectiondataID }) {
//   const [locations, setLocations] = useState([]);
//   const [showForm, setShowForm] = useState(false);
 
//   const { data, isError , isSuccess } = useSelector((s) => s.GetTransportDetail); 

//   const dispatch = useDispatch();          

//   useEffect(() => {
//     if (projectiondataID && !data) {
//       dispatch(GetTransportDetail(projectiondataID));
//     }
//   }, [dispatch, projectiondataID]);
//   useEffect(()=>{
//     // if(data){
//       // console.log(`this is transport data ${JSON.stringify(data , null , " ")}`)
//       setLocations(data?.data || [])
//     // }
//   },[isSuccess])

//   // console.log(`this is transport data ${JSON.stringify(locations , null , "")}`)

//   const handleAddTransport = (newLocation) => {
//     const formattedDistance = `${newLocation.distance} km`;

//     setLocations((prev) => [
//       ...prev,
//       {
//         type: newLocation.type,
//         name: newLocation.name,
//         distance: formattedDistance,
//       }
//     ]);
//     const transportData = {
//       transport_detail: [
//         ...locations.map((loc) => ({
//           type: loc.type,
//           name: loc.name,
//           Distance: parseFloat(loc.distance.split(' ')[0])
//         })),
//         {
//           type: newLocation.type,
//           name: newLocation.name,
//           Distance: parseFloat(newLocation.distance)
//         }
//       ]
//     };

//     console.log("Data for backend:", transportData);
//   };

//   return (
//     <div className="p-4 text-white">
//       <div className="relative flex items-center mb-4">
//         <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//           Transportation Access
//         </h3>
//         <button
//           onClick={() => setShowForm(true)}
//           className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//           </svg>
//         </button>
//       </div>

//       <TransportAccessForm
//         showForm={showForm}
//         setShowForm={setShowForm}
//         onAddTransport={handleAddTransport}
//         projectiondataID={projectiondataID}
//       />

//       <div className="flex justify-center">
        
//         {  locations  && locations?.transport_detail  ? (
//           <table className="border border-gray-600 text-sm">
//             <thead className="bg-gray-800 text-gray-200">
//               <tr>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//               </tr>
//             </thead>
//             <tbody>
//               {locations  && locations?.transport_detail.map((location, index) => (
//                 <tr key={index} className="hover:bg-gray-700">
//                   <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//            No transport details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default TransportAccess;

// use this code if have any 
// import React, { useEffect, useState } from 'react';
// import TransportAccessForm from './TransportAccessForm';
// import { useDispatch, useSelector } from 'react-redux';
// import { GetTransportDetail } from '../../../FeatureRedux/ProjectionReducer/TransportProjectionReducer/getTransport_Projection_reducre';

// function TransportAccess({projectiondataID}) {
//   const [locations, setLocations] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const {data , isError} = useSelector((s) => s.GetTransportDetail.data);


//   // console.log(`thisis transport projection detail ${JSON.stringify(data , null , " ")}`);
//   const dispatch = useDispatch()

//   useEffect(()=>{
//     dispatch(GetTransportDetail(projectiondataID))
//   },[dispatch])
  
//   const handleAddTransport = (newLocation) => {
//     const formattedDistance = `${newLocation.distance} km`;
    
//     setLocations(prev => [
//       ...prev,
//       {
//         type: newLocation.type,
//         name: newLocation.name,
//         distance: formattedDistance,
//         direction: ''
//       }
//     ]);
    
//     // Prepare data for backend in the required format
//     const transportData = {
//       transport_detail: [
//         ...locations.map(loc => ({
//           type: loc.type,
//           name: loc.name,
//           Distance: parseFloat(loc.distance.split(' ')[0])
//         })),
//         {
//           type: newLocation.type,
//           name: newLocation.name,
//           Distance: parseFloat(newLocation.distance)
//         }
//       ]
//     };
    
//     console.log("Data for backend:", transportData);
//   };




//   return (
//     <div className="p-4 text-white">
//      <div className="relative flex items-center mb-4">
//   <h3 className="text-xl font-semibold absolute left-1/2 transform -translate-x-1/2">
//     Transportation Access
//   </h3>
//   <button 
//     onClick={() => setShowForm(true)}
//     className="ml-auto bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//   >
//     <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//       <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//     </svg>
//   </button>
// </div>


//       <TransportAccessForm 
//         showForm={showForm} 
//         setShowForm={setShowForm} 
//         onAddTransport={handleAddTransport} 
//         projectiondataID={projectiondataID}
//       />

//       <div className="flex justify-center">
//         {locations.length > 0 ? (
//           <table className="border border-gray-600 text-sm">
//             <thead className="bg-gray-800 text-gray-200">
//               <tr>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
              
//               </tr>
//             </thead>
//             <tbody>
//               {locations.map((location, index) => (
//                 <tr key={index} className="hover:bg-gray-700">
//                   <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
        
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//             No transport details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default TransportAccess;


// import React, { useState } from 'react';

// function NearestLocation() {
//   const [locations, setLocations] = useState([]);
//   const [showForm, setShowForm] = useState(false);
//   const [newLocation, setNewLocation] = useState({
//     type: '',
//     name: '',
//     distance: '',
//   });

//   const handleInputChange = (e) => {
//     const { name, value } = e.target;
//     setNewLocation(prev => ({
//       ...prev,
//       [name]: value
//     }));
//   };

//   const handleSubmit = (e) => {
//     e.preventDefault();
//     const formattedDistance = `${newLocation.distance} km`;
    
//     setLocations(prev => [
//       ...prev,
//       {
//         type: newLocation.type,
//         name: newLocation.name,
//         distance: formattedDistance,
//         direction: '' // Keeping direction field for table display but empty
//       }
//     ]);
    
//     // Prepare data for backend in the required format
//     const transportData = {
//       transport_detail: locations.map(loc => ({
//         type: loc.type,
//         name: loc.name,
//         Distance: parseFloat(loc.distance.split(' ')[0]) // Extract numeric value from "X km"
//       }))
//     };
    
//     console.log("Data for backend:", transportData);
    
//     setNewLocation({
//       type: '',
//       name: '',
//       distance: '',
//     });
//     setShowForm(false);
//   };

//   const closeForm = (e) => {
//     if (e.target === e.currentTarget) {
//       setShowForm(false);
//     }
//   };

//   return (
//     <div className="p-4 text-white">
//       <div className="flex justify-between items-center mb-4">
//         <h2 className="text-xl font-semibold text-center">Transportation Access</h2>
//         <button 
//           onClick={() => setShowForm(true)}
//           className="bg-blue-500 hover:bg-blue-600 text-white p-2 rounded-full"
//         >
//           <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
//             <path fillRule="evenodd" d="M10 5a1 1 0 011 1v3h3a1 1 0 110 2h-3v3a1 1 0 11-2 0v-3H6a1 1 0 110-2h3V6a1 1 0 011-1z" clipRule="evenodd" />
//           </svg>
//         </button>
//       </div>

//       {showForm && (
//         <div 
//           className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
//           onClick={closeForm}
//         >
//           <div 
//             className="bg-gray-800 p-6 rounded-lg w-full max-w-md relative"
//             onClick={e => e.stopPropagation()}
//           >
//             <button 
//               onClick={() => setShowForm(false)}
//               className="absolute top-2 right-2 text-gray-400 hover:text-white"
//             >
//               <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
//                 <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
//               </svg>
//             </button>
//             <h3 className="text-lg font-semibold mb-4">Add New Transport Detail</h3>
//             <form onSubmit={handleSubmit}>
//               <div className="mb-4">
//                 <label className="block text-gray-300 mb-2">Type</label>
//                 <input
//                   type="text"
//                   name="type"
//                   value={newLocation.type}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//                   required
//                 />
//               </div>
//               <div className="mb-4">
//                 <label className="block text-gray-300 mb-2">Name</label>
//                 <input
//                   type="text"
//                   name="name"
//                   value={newLocation.name}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//                   required
//                 />
//               </div>
//               <div className="mb-4">
//                 <label className="block text-gray-300 mb-2">Distance (in km)</label>
//                 <input
//                   type="number"
//                   name="distance"
//                   value={newLocation.distance}
//                   onChange={handleInputChange}
//                   className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
//                   step="0.1"
//                   required
//                 />
//               </div>
//               <button
//                 type="submit"
//                 className="w-full bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-md"
//               >
//                 Add Transport
//               </button>
//             </form>
//           </div>
//         </div>
//       )}

//       <div className="flex justify-center">
//         {locations.length > 0 ? (
//           <table className="border border-gray-600 text-sm">
//             <thead className="bg-gray-800 text-gray-200">
//               <tr>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//                 <th className="border border-gray-600 px-4 py-2 text-left">Direction</th>
//               </tr>
//             </thead>
//             <tbody>
//               {locations.map((location, index) => (
//                 <tr key={index} className="hover:bg-gray-700">
//                   <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
//                   <td className="border border-gray-600 px-4 py-2">{location.direction}</td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         ) : (
//           <div className="text-center py-8 text-gray-400">
//             No transport details added yet. Click the + button to add some.
//           </div>
//         )}
//       </div>
//     </div>
//   );
// }

// export default NearestLocation;


// import React from 'react';

// function NearestLocation() {
//   const locations = [
//     { type: 'Railway Station', name: 'ABC Junction', distance: '2.5 km', direction: 'North-East' },
//     { type: 'Airport', name: 'XYZ International Airport', distance: '12 km', direction: 'South-West' },
//     { type: 'Bus Stand', name: 'Central Bus Stand', distance: '1.2 km', direction: 'West' },
//     { type: 'Highway', name: 'NH-44', distance: '3.8 km', direction: 'East' },
//   ];

//   return (
//     <div className="p-4 text-white">
//       <h2 className="text-xl font-semibold mb-4 text-center">Transportation Access</h2>
//       <div className="flex justify-center">
//         <table className="border border-gray-600 text-sm">
//           <thead className="bg-gray-800 text-gray-200">
//             <tr>
//               <th className="border border-gray-600 px-4 py-2 text-left">Type</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Name</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Distance</th>
//               <th className="border border-gray-600 px-4 py-2 text-left">Direction</th>
//             </tr>
//           </thead>
//           <tbody>
//             {locations.map((location, index) => (
//               <tr key={index} className="hover:bg-gray-700">
//                 <td className="border border-gray-600 px-4 py-2">{location.type}</td>
//                 <td className="border border-gray-600 px-4 py-2">{location.name}</td>
//                 <td className="border border-gray-600 px-4 py-2">{location.distance}</td>
//                 <td className="border border-gray-600 px-4 py-2">{location.direction}</td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// }

// export default NearestLocation;

// import React from 'react';

// function NearestLocation() {
//   const locations = [
//     { type: 'Railway Station', name: 'ABC Junction', distance: '2.5 km' },
//     { type: 'Airport', name: 'XYZ International Airport', distance: '12 km' },
//     { type: 'Bus Stand', name: 'Central Bus Stand', distance: '1.2 km' },
//     { type: 'Highway', name: 'NH-44', distance: '3.8 km' },
//   ];

//   return (
//     <div className="p-6 bg-[#1f2937] rounded-lg shadow-md text-white">
//       {/* <h2 className="text-xl font-bold mb-4">Nearest Locations</h2> */}
//       <table className="w-full border border-gray-700 rounded-lg overflow-hidden">
//         <thead className="bg-[#374151]">
//           <tr>
//             <th className="border border-gray-700 px-4 py-2 text-left">Type</th>
//             <th className="border border-gray-700 px-4 py-2 text-left">Name</th>
//             <th className="border border-gray-700 px-4 py-2 text-left">Distance</th>
//           </tr>
//         </thead>
//         <tbody>
//           {locations.map((location, index) => (
//             <tr key={index} className="hover:bg-[#2d3748]">
//               <td className="border border-gray-700 px-4 py-2">{location.type}</td>
//               <td className="border border-gray-700 px-4 py-2">{location.name}</td>
//               <td className="border border-gray-700 px-4 py-2">{location.distance}</td>
//             </tr>
//           ))}
//         </tbody>
//       </table>
//     </div>
//   );
// }

// export default NearestLocation;
